import { useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react';

/**
 * Shared modal primitive.
 * - Backdrop = `rgba(0,0,0,0.5)`.
 * - Content = white panel, 0.5rem radius, `0 4px 15px rgba(0,0,0,0.2)` shadow.
 * - Close = absolute-positioned "×" in dark gray.
 * - Traps focus while open and restores it on close.
 */

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'full';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: ModalSize;
  /** Close when the backdrop (not the content) is clicked. Default: true. */
  closeOnBackdrop?: boolean;
  /** Close when Escape is pressed. Default: true. */
  closeOnEscape?: boolean;
  /** Render the close (×) button in the header. Default: true. */
  showCloseButton?: boolean;
  /** Optional footer region. Convention: action buttons right-aligned. */
  footer?: ReactNode;
  children?: ReactNode;
  ariaLabel?: string;
  /** ID of element labelling the dialog (overrides aria-label). */
  labelledBy?: string;
  /** Element to focus on open. */
  initialFocus?: { current: HTMLElement | null };
  /** Optional class names appended to the scrollable content div. */
  contentClassName?: string;
}

const SIZE_WIDTH: Record<ModalSize, string> = {
  sm: 'w-[400px]',
  md: 'w-[600px]',
  lg: 'w-[800px]',
  xl: 'w-[1000px]',
  full: 'w-[95vw] h-[95vh]',
};

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('aria-hidden'),
  );
}

export function Modal({
  open,
  onClose,
  title,
  size = 'md',
  closeOnBackdrop = true,
  closeOnEscape = true,
  showCloseButton = true,
  footer,
  children,
  ariaLabel,
  contentClassName,
}: ModalProps): JSX.Element | null {
  const contentRef = useRef<HTMLDivElement>(null);
  const previouslyFocused = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;

    previouslyFocused.current = document.activeElement as HTMLElement | null;
    document.body.classList.add('overflow-hidden');

    const frame = requestAnimationFrame(() => {
      const root = contentRef.current;
      if (!root) return;
      const focusables = getFocusable(root);
      (focusables[0] ?? root).focus();
    });

    return () => {
      cancelAnimationFrame(frame);
      document.body.classList.remove('overflow-hidden');
      previouslyFocused.current?.focus?.();
    };
  }, [open]);

  useEffect(() => {
    if (!open || !closeOnEscape) return;
    function handleKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open, closeOnEscape, onClose]);

  const handleKeyDown = useCallback((e: ReactKeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return;
    const root = contentRef.current;
    if (!root) return;
    const focusables = getFocusable(root);
    if (focusables.length === 0) {
      e.preventDefault();
      root.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }, []);

  const handleBackdrop = useCallback(
    (e: ReactMouseEvent<HTMLDivElement>) => {
      if (!closeOnBackdrop) return;
      if (e.target === e.currentTarget) onClose();
    },
    [closeOnBackdrop, onClose],
  );

  if (!open) return null;

  const sizeClass = SIZE_WIDTH[size];

  return createPortal(
    <div
      role="presentation"
      onClick={handleBackdrop}
      className="fixed inset-0 z-[1000] flex items-center justify-center p-4 bg-black/50"
    >
      <div
        ref={contentRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : ariaLabel}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={`bg-white rounded-lg shadow-[0_4px_15px_rgba(0,0,0,0.2)] max-w-[90vw] max-h-[90vh] overflow-hidden relative flex flex-col focus:outline-none font-[var(--font-body)] ${sizeClass}`}
      >
        {title ? (
          <div className="flex items-center justify-between px-6 py-4 border-b border-[color:var(--gray-200)]">
            <h3 className="m-0 font-[var(--font-heading)] text-xl text-[color:var(--text-dark)]">
              {title}
            </h3>
            {showCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close modal"
                className="w-8 h-8 flex items-center justify-center bg-transparent border-none text-2xl text-[color:var(--text-dark)] hover:text-[color:var(--accent)] cursor-pointer transition-colors leading-none"
              >
                &times;
              </button>
            )}
          </div>
        ) : (
          showCloseButton && (
            // Title-less modals: float the close button at top-right.
            <button
              type="button"
              onClick={onClose}
              aria-label="Close modal"
              className="absolute top-2 right-4 z-10 w-8 h-8 flex items-center justify-center bg-transparent border-none text-2xl text-[color:var(--text-dark)] hover:text-[color:var(--accent)] cursor-pointer transition-colors leading-none"
            >
              &times;
            </button>
          )
        )}
        <div className={`p-6 overflow-y-auto max-h-[60vh] text-[color:var(--text-dark)] leading-relaxed${contentClassName ? ` ${contentClassName}` : ""}`}>
          {children}
        </div>
        {footer ? (
          <div className="flex justify-end gap-2 px-6 py-4 border-t border-[color:var(--gray-200)]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export default Modal;
